<template>
  <div class="course-detail-container">
    <div class="header">
      <van-nav-bar
        title="详情"
        left-text="返回"
        left-arrow
        @click-left="onClickLeft"
      />
    </div>

    <div class="content">
      <div class="profile-card" v-if="detailInfo.candidaterProfile">
        <div class="avatar-section">
          <div class="avatar-container" @click="goToUserInfo">
            <img
              v-if="detailInfo.candidaterProfile?.avatar"
              :src="getFileFullUrl(detailInfo.candidaterProfile?.avatar)"
              alt="头像"
              class="avatar-img"
            />
            <div v-else class="avatar-placeholder">
              <van-icon name="user-o" size="40" />
            </div>
          </div>
        </div>
        <div class="info-section">
          <div class="info-row">
            <span class="label"
              >姓名:{{ detailInfo.candidaterProfile?.name }}</span
            >
          </div>
          <div class="info-row">
            <span class="label"
              >身份证号:{{ detailInfo.candidaterProfile?.idCard }}</span
            >
          </div>
          <!-- <div class="info-row">
            <span class="label">类型：{{ getcardText(detailInfo.candidaterProfile?.idCardType) }}</span>
          </div> -->
          <div class="info-row">
            <span class="label">课程名称：{{ detailInfo.title || "-" }}</span>
          </div>
        </div>
      </div>

      <div class="progress-card" v-if="detailInfo.step">
        <div class="progress-steps">
          <!-- optionCourseExam为true时 为选修考试，考试步骤可以点击 -->
          <div
            class="step-item"
            :class="{
              active:
                originStep >= index + 1 ||
                (isOptionalExam && hasRegistered && step.key === 'EXAM'),
              current: currentStep === index + 1,
              disabled: isRejected && step.key !== 'REGISTRATION',
            }"
            v-for="(step, index) in steps"
            :key="step.title"
            @click="handleStepClick(index)"
          >
            <!--  -->
            <div class="step-circle"></div>
            <div class="step-label">{{ step.title }}</div>
          </div>
        </div>
      </div>

      <!-- 报名 -->
      <SignUp
        v-if="currentStep === 1"
        :detailInfo="detailInfo"
        @submitSuccess="getExamDetail"
      />
      <!-- 学习 -->
      <Study v-if="currentStep === 2" :detailInfo="detailInfo" />
      <!-- 考试 -->
      <Examination v-if="currentStep === 3" :detailInfo="detailInfo" />
      <!-- 通过/证书 -->
      <Pass
        v-if="currentStep === 4"
        :detailInfo="detailInfo"
        :refresh-data="getExamDetail"
        @refresh="getExamDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import SignUp from "./components/SignUp.vue";
import Study from "./components/Study.vue";
import Examination from "./components/Examination.vue";
import Pass from "./components/Pass.vue";
import { get } from "@/request.js";
import { getFileFullUrl, idTypeOptions } from "@/utils";

const router = useRouter();
const route = useRoute();

const currentStep = ref(1); // 当前进度步骤
const originStep = ref(1); // 原始进度步骤
const registrationId = ref("");
const courseId = ref("");
const isOptionalExam = ref(false);
const hasRegistered = computed(() => {
  // 已报名：当前流程不在 REGISTRATION 阶段
  return detailInfo.value?.step && detailInfo.value.step !== "REGISTRATION";
});
const userInfo = reactive({
  avatar: "",
  name: "",
  phone: "",
  type: "",
});
// 测试数据
const detailInfo = ref({});

const getcardText = (value) => {
  return idTypeOptions.find((item) => item.value === value)?.label || "-";
};

// 状态配置（与 examList 同步）
const examStatus = ref({
  // 审核状态
  FILLING_IN: { text: "报名中", color: "#1890ff" },
  PENDING: { text: "待审核", color: "#faad14" },
  APPROVED: { text: "已报名", color: "#52c41a" },
  REJECTED: { text: "已拒绝", color: "#ff4d4f" },
  // 学习进度状态
  STUDING: { text: "学习", color: "#1890ff" },
  EXAM: { text: "考试", color: "#f50" },
  PASSED: { text: "完成", color: "#1890ff" },
});

// 判断是否被拒绝
const isRejected = computed(() => {
  return detailInfo.value?.auditStatus === "REJECTED";
});

// 获取应该显示的步骤
const getDisplayStep = (detailInfo) => {
  // 优先使用 auditStatus
  if (detailInfo?.auditStatus) {
    // 如果 auditStatus 是通过（APPROVED），则显示 step（学习进度状态）
    if (detailInfo.auditStatus === "APPROVED") {
      return detailInfo.step;
    }
    // 否则显示 REGISTRATION（还在审核阶段，停留在报名步骤）
    return "REGISTRATION";
  }
  // 如果没有 auditStatus，使用 step（学习进度状态）
  return detailInfo?.step || "REGISTRATION";
};

const steps = ref([
  {
    title: "报名",
    key: "REGISTRATION",
    isActive: false,
    step: 1,
  },
  {
    title: "学习",
    key: "STUDING",

    isActive: false,
    step: 2,
  },
  {
    title: "考试",
    isActive: false,
    key: "EXAM",
    step: 3,
  },
  {
    title: "完成",
    isActive: false,
    key: "PASSED",
    step: 4,
  },
]);

onMounted(() => {
  // 考试id
  registrationId.value = route.query.id || "";

  // 🔥 修复：检查是否需要刷新（从考试页面返回时）
  // 由于路由没有使用 keep-alive，组件每次都是重新创建的，所以需要在 onMounted 中检查刷新标记
  const needRefresh = localStorage.getItem("needRefreshCourseDetail");
  if (needRefresh === "true") {
    localStorage.removeItem("needRefreshCourseDetail");
    // 刷新课程详情数据
    getExamDetail().then(() => {
      // 🔥 修复：在 getExamDetail 完成后，如果路由参数中有 step，优先使用路由参数设置当前步骤
      // 这样可以确保从考试页面返回时能正确显示考试列表（步骤3）
      if (route.query.step) {
        const stepNum = parseInt(route.query.step);
        if (stepNum >= 1 && stepNum <= 4) {
          currentStep.value = stepNum;
        }
      }
    });
  } else {
    // 正常加载数据
    getExamDetail().then(() => {
      // 🔥 修复：在 getExamDetail 完成后，如果路由参数中有 step，优先使用路由参数设置当前步骤
      // 这样可以确保从考试页面返回时能正确显示考试列表（步骤3）
      if (route.query.step) {
        const stepNum = parseInt(route.query.step);
        if (stepNum >= 1 && stepNum <= 4) {
          currentStep.value = stepNum;
        }
      }
    });
  }
});

// 页面激活时检查是否需要刷新（从考试页面返回时，用于 keep-alive 场景）
onActivated(() => {
  const needRefresh = localStorage.getItem("needRefreshCourseDetail");
  if (needRefresh === "true") {
    localStorage.removeItem("needRefreshCourseDetail");
    // 刷新课程详情数据
    getExamDetail().then(() => {
      // 🔥 修复：在 getExamDetail 完成后，如果路由参数中有 step，优先使用路由参数设置当前步骤
      if (route.query.step) {
        const stepNum = parseInt(route.query.step);
        if (stepNum >= 1 && stepNum <= 4) {
          currentStep.value = stepNum;
        }
      }
    });
  }
});
const getExamDetail = async () => {
  const res = await get(`/api/my/registration/${registrationId.value}`);
  if (res.success) {
    detailInfo.value = res.data;
    const optionCourseExam = res.data.optionCourseExam || false;
    // optionCourseExam为true时 为选修考试，考试步骤可以点击
    isOptionalExam.value = optionCourseExam;

    // 根据 auditStatus 决定显示的步骤（与 examList 同步）
    const displayStep = getDisplayStep(detailInfo.value);

    steps.value.forEach((item) => {
      if (item.key === displayStep) {
        item.isActive = true;
      }
    });

    // 🔥 修复：如果路由参数中有 step，优先使用路由参数，否则使用计算出的步骤
    // 这样可以确保从考试页面返回时能正确显示考试列表（步骤3）
    if (route.query.step) {
      const stepNum = parseInt(route.query.step);
      if (stepNum >= 1 && stepNum <= 4) {
        currentStep.value = stepNum;
      } else {
        currentStep.value =
          steps.value.find((item) => item.isActive)?.step || 1;
      }
    } else {
      currentStep.value = steps.value.find((item) => item.isActive)?.step || 1;
    }
    originStep.value = currentStep.value;
  }
  return res;
};

const handleStepClick = (index) => {
  const step = steps.value[index];
  if (!step) return;
  // 如果被拒绝，且不是报名步骤，不允许点击
  if (isRejected.value && step.key !== "REGISTRATION") {
    return;
  }
  // 选修考试且已报名：允许直接点击"考试"步骤
  if (isOptionalExam.value && hasRegistered.value && step.key === "EXAM") {
    currentStep.value = step.step;
    return;
  }
  // 其它步骤：只能点击已完成的步骤
  if (index + 1 > originStep.value) {
    return;
  }
  currentStep.value = index + 1;
  // 切换到考试步骤时刷新数据，确保显示最新状态
  if (index + 1 === 3) {
    getExamDetail();
  }
};
// 返回上一页
const onClickLeft = () => {
  // 固定返回到课程列表页
  router.push({
    path: "/mobileHome",
  });
};

// 跳转到个人信息页面
const goToUserInfo = () => {
  router.push({
    path: "/userInfo",
  });
};
</script>

<style scoped lang="less">
.course-detail-container {
  min-height: 100vh;
  height: 100%;
  overflow-y: scroll;

  .header {
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .content {
    padding: 16px;

    // 个人信息卡片
    .profile-card {
      background: white;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;

      .avatar-section {
        margin-right: 20px;

        .avatar-container {
          width: 80px;
          height: 80px;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #e0e7ff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;

          &:active {
            transform: scale(0.95);
          }

          .avatar-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .avatar-placeholder {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
        }
      }

      .info-section {
        flex: 1;

        .info-row {
          margin-bottom: 12px;

          &:last-child {
            margin-bottom: 0;
          }

          .label {
            font-size: 16px;
            color: #333;
            font-weight: 400;
          }
        }
      }
    }

    // 进度状态卡片
    .progress-card {
      background: white;
      border-radius: 12px;
      padding: 24px 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

      .progress-steps {
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;

        &::before {
          content: "";
          position: absolute;
          top: 14px; // step-circle 高度 30px，中心在 15px，线条高度 2px，所以 top = 15px - 1px = 14px
          left: 20px;
          right: 20px;
          height: 2px;
          background: #e0e0e0;
          z-index: 1;
        }

        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;

          .step-circle {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 8px;
            transition: all 0.3s ease;
          }

          .step-label {
            font-size: 14px;
            color: #666;
            font-weight: 500;
          }

          &.active {
            .step-circle {
              color: #ffffff !important;
            }

            .step-label {
              color: #333;
              font-weight: 600;
            }
          }

          // 已完成步骤使用绿色
          &:nth-child(1).active .step-circle {
            background: #b3e766;
          }
          &:nth-child(2).active .step-circle {
            background: #b3e766;
          }
          &:nth-child(3).active .step-circle {
            background: #b3e766;
          }
          &:nth-child(4).active .step-circle {
            background: #b3e766;
          }

          // 当前步骤使用蓝色渐变
          &.current .step-circle {
            background: linear-gradient(135deg, #1890ff, #36cfc9) !important;
          }

          // 被拒绝时，学习/考试/完成步骤置灰且不可点击
          &.disabled {
            cursor: not-allowed;

            .step-circle {
              background: #d9d9d9 !important;
            }

            .step-label {
              color: #999 !important;
            }
          }
        }
      }
    }
  }
}

// 全局样式覆盖
:deep(.van-nav-bar) {
  .van-nav-bar__title {
    font-size: 18px;
    font-weight: 600;
  }
}
</style>
